import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowLeft, ArrowRight, Check, Compass, RotateCcw, X } from 'lucide-react';
import type { Page } from '../types';
import { lockBodyScroll, unlockBodyScroll } from '../lib/scroll-lock';

export interface TourStep {
  page: Page;
  title: string;
  text: string;
  basket?: boolean;
}

export const TOUR_STEPS: TourStep[] = [
  {
    page: 'overview',
    title: 'Dit revir, samlet ét sted',
    text: 'Opret og redigér revirer med de samme formularer som i din egen arbejdsplads.',
  },
  {
    page: 'map',
    title: 'Tegn og redigér dine marker',
    text: 'Prøv kortets tegneværktøj. Du kan ændre form, navn og frøblanding på en mark.',
  },
  {
    page: 'catalog',
    title: 'Find blandingen til din mark',
    text: 'Se frøblandingerne og vælg dem til markerne, præcis som i produktet.',
  },
  {
    page: 'plans',
    title: 'Planlæg sæsonen',
    text: 'Opret en plejeplan, vælg sæson og afprøv planens status.',
  },
  {
    page: 'biotope-plan',
    title: 'Biotopplan & 25 tiltag',
    text: 'Se de officielle SGAV-krav, pointberegning og biotopplanlægning for reviret.',
  },
  {
    page: 'seeding',
    title: 'Såningsgennemgang',
    text: 'Følg live jordtemperatur og 14-dages så-prognose for optimale såbetingelser.',
  },
  {
    page: 'map',
    basket: true,
    title: 'Fra mark til indkøbsliste',
    text: 'Se det beregnede frøbehov. Overførsel til webshoppen er kun en demonstration.',
  },
];

export interface ProductTourProps {
  onExit: () => void;
  onLogin: () => void;
  step?: number;
  onStepChange?: (step: number) => void;
  onReset?: () => void;
  skipIntro?: boolean;
  children?: ReactNode;
}

export default function ProductTour({
  onExit,
  onLogin,
  step: controlledStep,
  onStepChange,
  onReset,
  skipIntro = false,
  children,
}: ProductTourProps) {
  const [intro, setIntro] = useState(!skipIntro);
  const [guided, setGuided] = useState(skipIntro);
  const [internalStep, setInternalStep] = useState(0);
  const dialog = useRef<HTMLElement>(null);
  const start = useRef<HTMLButtonElement>(null);
  const shell = useRef<HTMLDivElement>(null);

  const step = controlledStep !== undefined ? controlledStep : internalStep;

  const setStep = (updater: number | ((prev: number) => number)) => {
    const nextVal = typeof updater === 'function' ? updater(step) : updater;
    if (onStepChange) {
      onStepChange(nextVal);
    } else {
      setInternalStep(nextVal);
    }
  };

  // Preload map and workspace chunks immediately so step transitions are instant on mobile
  useEffect(() => {
    void import('./FieldMap');
    void import('./WorkspacePages');
  }, []);

  // Scroll smoothly to top on step transition so the new page is never scrolled to bottom
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const mainWorkspace = document.querySelector('.main-workspace, .demo-workspace');
    if (mainWorkspace) {
      mainWorkspace.scrollTop = 0;
    }
  }, [step]);

  useEffect(() => {
    if (intro) start.current?.focus();
    else shell.current?.focus();
  }, [intro]);

  useEffect(() => {
    if (!intro) return;
    lockBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  }, [intro]);

  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const hasOpenOverlay = Boolean(
        document.querySelector(
          '.drawer-overlay, .modal-overlay, .modal-backdrop, .sidebar.is-open, .nav-scrim, [role="dialog"]:not(.tour-intro-dialog), .superadmin-customer-drawer, .superadmin-drawer-backdrop',
        ),
      );
      if (event.key === 'Escape' && (intro || !hasOpenOverlay)) {
        onExit();
        return;
      }
      if (!intro || event.key !== 'Tab') return;
      const controls = Array.from(
        dialog.current?.querySelectorAll<HTMLButtonElement>('button') ?? [],
      );
      const first = controls[0];
      const last = controls.at(-1);
      if (!dialog.current?.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [intro, onExit]);

  const reset = () => {
    if (onReset) onReset();
    setStep(0);
    setGuided(false);
    setIntro(true);
  };

  const current = TOUR_STEPS[step] || TOUR_STEPS[0];

  return (
    <div
      className={`demo-shell ${guided ? 'is-guided' : ''}`}
      ref={shell}
      tabIndex={-1}
      aria-label="Plotwise rundtur"
    >
      <header className="demo-banner" inert={intro}>
        <div className="demo-banner-brand">
          <strong className="demo-badge">Rundtur</strong>
          <span className="demo-badge-sub">Eksempeldata · intet gemmes</span>
        </div>
        <div className="demo-banner-actions">
          {!guided && !intro && (
            <button
              onClick={() => setGuided(true)}
              aria-label="Genoptag rundtur"
              title="Genoptag rundtur"
              className="demo-action-btn demo-action-resume"
              style={{
                background: '#1b3b22',
                color: '#ffffff',
                border: '1px solid #2d5a36',
                fontWeight: 600,
              }}
            >
              <Compass size={15} />
              <span>Vis guide</span>
            </button>
          )}
          <button
            onClick={reset}
            aria-label="Start forfra"
            title="Start forfra"
            className="demo-action-btn demo-action-reset"
          >
            <RotateCcw size={16} />
          </button>
          <button onClick={onLogin} className="demo-action-btn demo-action-login">
            <span>Log ind</span>
            <ArrowRight size={14} />
          </button>
          <button
            className="demo-action-btn demo-return"
            onClick={onExit}
            aria-label="Tilbage til forsiden"
          >
            <ArrowLeft size={15} />
            <span className="demo-return-text-desktop">Tilbage til forsiden</span>
            <span className="demo-return-text-mobile">Luk</span>
          </button>
        </div>
      </header>

      <div className="demo-workspace" inert={intro}>
        {children}
      </div>

      {guided && (
        <footer className="demo-guide" inert={intro} aria-label="Rundturens trin">
          <button
            type="button"
            className="demo-guide-close"
            aria-label="Luk guiden"
            title="Luk guiden"
            onClick={() => setGuided(false)}
          >
            <X size={18} />
          </button>
          <div className="demo-guide-info">
            <span className="demo-guide-step-tag">
              TRIN {step + 1} AF {TOUR_STEPS.length}
            </span>
            <strong>{current.title}</strong>
            <p>{current.text}</p>
          </div>
          <div className="demo-guide-actions">
            <button
              type="button"
              className="demo-guide-explore-btn"
              onClick={() => setGuided(false)}
            >
              Udforsk selv
            </button>
            <div className="demo-guide-nav-group">
              {step > 0 && (
                <button
                  type="button"
                  className="demo-guide-nav-btn demo-guide-prev-btn"
                  aria-label="Forrige trin"
                  onClick={() => setStep((value) => value - 1)}
                >
                  <ArrowLeft size={16} />
                </button>
              )}
              <button
                type="button"
                className="button primary demo-guide-nav-btn demo-guide-next-btn"
                onClick={() =>
                  step === TOUR_STEPS.length - 1 ? onExit() : setStep((value) => value + 1)
                }
              >
                {step === TOUR_STEPS.length - 1 ? (
                  <>
                    <span>Afslut rundtur</span>
                    <Check size={16} />
                  </>
                ) : (
                  <>
                    <span>Næste</span>
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        </footer>
      )}

      {intro && (
        <div className="tour-intro-backdrop">
          <section
            ref={dialog}
            className="tour-intro-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tour-intro-title"
            aria-describedby="tour-intro-description"
          >
            <button className="tour-intro-close" aria-label="Afslut rundtur" onClick={onExit}>
              <X size={20} />
            </button>
            <span className="tour-intro-kicker">DIN PLOTWISE-ARBEJDSPLADS</span>
            <h2 id="tour-intro-title">Se hvordan Plotwise samler dit revir</h2>
            <p id="tour-intro-description">
              Afprøv de samme kort, formularer og værktøjer som en kunde. Dine ændringer og filer
              forsvinder, når du afslutter eller genindlæser rundturen.
            </p>
            <div className="tour-intro-actions">
              <button
                ref={start}
                className="button primary tour-intro-start-btn"
                onClick={() => {
                  setGuided(true);
                  setIntro(false);
                }}
              >
                <span>Start rundtur</span>
                <ArrowRight size={17} />
              </button>
              <button
                className="button secondary tour-intro-explore-btn"
                onClick={() => {
                  setGuided(false);
                  setIntro(false);
                }}
              >
                <Compass size={16} />
                <span>Udforsk selv</span>
              </button>
            </div>
            <div className="tour-intro-footer-actions">
              <button type="button" className="tour-intro-login" onClick={onExit}>
                Tilbage til forsiden
              </button>
              <button type="button" className="tour-intro-login" onClick={onLogin}>
                Har du allerede en konto?&nbsp;<strong>Log ind</strong>
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
